import React, { useState } from 'react';
import { Copy, Trash2 } from 'lucide-react';
import { Label } from './ui/label.jsx';
import { Button } from './ui/button.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select.jsx';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog.jsx';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfileSelector = ({ profiles, currentConfigId, onLoadProfile, onLoadAsTemplate, onRefresh, isNewConfigMode }) => {
  const { toast } = useToast();
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState(null);

  const handleLoadProfile = () => {
    if (selectedProfileId) {
      onLoadProfile(selectedProfileId);
    }
  };

  const handleLoadAsTemplate = () => {
    if (selectedProfileId) {
      onLoadAsTemplate(selectedProfileId);
    }
  };

  const handleDuplicate = async () => {
    if (!selectedProfileId) {
      toast({
        title: 'Error',
        description: 'Please select a profile to duplicate',
        variant: 'destructive'
      });
      return;
    }

    const profile = profiles.find(p => p.id === selectedProfileId);
    const newName = prompt('Enter new profile name:', `${profile.profile_name} (Copy)`);
    
    if (!newName) return;

    try {
      await axios.post(`${API}/configurations/${selectedProfileId}/duplicate?new_profile_name=${encodeURIComponent(newName)}`);
      await onRefresh();
      toast({
        title: 'Success',
        description: 'Profile duplicated successfully'
      });
    } catch (error) {
      console.error('Error duplicating profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to duplicate profile',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteClick = () => {
    if (!selectedProfileId) {
      toast({
        title: 'Error',
        description: 'Please select a profile to delete',
        variant: 'destructive'
      });
      return;
    }
    
    const profile = profiles.find(p => p.id === selectedProfileId);
    setProfileToDelete(profile);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/configurations/${profileToDelete.id}`);
      setSelectedProfileId('');
      await onRefresh();
      toast({
        title: 'Success',
        description: 'Profile deleted successfully'
      });
    } catch (error) {
      console.error('Error deleting profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete profile',
        variant: 'destructive'
      });
    } finally {
      setShowDeleteDialog(false);
      setProfileToDelete(null);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-4">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Load Profile</h2>
        <div className="space-y-3">
          <div>
            <Label htmlFor="profile-select">Select Profile</Label>
            <Select value={selectedProfileId} onValueChange={setSelectedProfileId}>
              <SelectTrigger data-testid="profile-selector">
                <SelectValue placeholder="Choose a saved configuration..." />
              </SelectTrigger>
              <SelectContent>
                {profiles.map(profile => (
                  <SelectItem key={profile.id} value={profile.id}>
                    {profile.profile_name} {profile.suite_number && `(Suite ${profile.suite_number})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={handleLoadProfile}
              disabled={!selectedProfileId}
              className={`w-full sm:flex-1 ${
                !isNewConfigMode && selectedProfileId 
                  ? 'ring-2 ring-blue-500 ring-offset-2 shadow-lg' 
                  : ''
              }`}
              data-testid="load-profile-btn"
              title={!isNewConfigMode ? 'Recommended: Load and replace current data' : 'Load and replace current data'}
            >
              Load & Replace
            </Button>
            <Button
              onClick={handleLoadAsTemplate}
              disabled={!selectedProfileId}
              variant="outline"
              className={`w-full sm:w-auto ${
                isNewConfigMode && selectedProfileId 
                  ? 'ring-2 ring-green-500 ring-offset-2 shadow-lg border-green-500 text-green-700 hover:text-green-800' 
                  : ''
              }`}
              data-testid="load-template-btn"
              title={isNewConfigMode ? 'Recommended: Load devices without changing your profile name' : 'Load devices without changing your profile name'}
            >
              Load as Template
            </Button>
            <Button
              onClick={handleDuplicate}
              disabled={!selectedProfileId}
              variant="outline"
              className="w-full sm:w-auto"
              data-testid="duplicate-profile-btn"
            >
              <Copy className="w-4 h-4 mr-1" />
              Duplicate
            </Button>
            <Button
              onClick={handleDeleteClick}
              disabled={!selectedProfileId}
              variant="outline"
              className="w-full sm:w-auto text-red-600 hover:text-red-700"
              data-testid="delete-profile-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Profile</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{profileToDelete?.profile_name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ProfileSelector;